// Blog posts in Markdown — SEO-focused for "Ley 21.719" keyword cluster
// Each post has a cover image at /blog/{slug}.png (generated via Nano Banana, served from /public/blog/)
export const BLOG_POSTS = [
  {
    slug: "que-es-ley-21719-proteccion-datos-pymes-chile",
    cover: "/blog/que-es-ley-21719-proteccion-datos-pymes-chile.png",
    title: "Ley N° 21.719 de Protección de Datos Personales: qué es y cómo afecta a tu PYME en Chile",
    description: "Guía completa de la Ley 21.719 para PYMEs chilenas: vigencia, obligaciones, multas y plazo de adecuación antes del 1 de diciembre de 2026.",
    keywords: "Ley 21.719, protección de datos personales Chile, PYME, datos personales, APDP, cumplimiento",
    date: "2026-02-10",
    readingMinutes: 8,
    author: "Equipo Ley de Datos Chile",
    excerpt: "El 1 de diciembre de 2026 entra en vigencia la Ley 21.719 de Protección de Datos Personales. Si tu PYME pide nombres, emails, RUTs o teléfonos, debes estar preparado. Esto es todo lo que necesitas saber.",
    content: `## Qué es la Ley N° 21.719

La **Ley N° 21.719 de Protección de Datos Personales** es la nueva normativa chilena que regula cómo las empresas pueden tratar los datos personales de sus clientes, trabajadores y proveedores. Reemplaza a la antigua Ley 19.628 (de 1999) y alinea a Chile con el estándar internacional **GDPR europeo**.

La ley **crea la Agencia de Protección de Datos Personales (APDP)**, un organismo con poder de fiscalización, sanción y interpretación, similar a la AEPD española o la CNIL francesa.

## Cuándo entra en vigencia

La **vigencia plena es el 1 de diciembre de 2026**. Eso significa que toda empresa chilena que trate datos personales —da igual su tamaño— debe estar adecuada para esa fecha.

## A quién aplica

Aplica a **toda persona natural o jurídica** que trate datos personales en Chile o de personas en Chile. En la práctica:

- E-commerce y tiendas online
- Restaurantes con sistema de reservas
- Inmobiliarias con formularios de contacto
- Servicios profesionales con CRM
- Cualquier empresa con sitio web que pida un email

Si tu negocio pide un dato a una persona, la ley te aplica.

## Qué obligaciones tiene tu PYME

La ley exige cinco cosas mínimas:

### 1. Política de Privacidad pública y real
Un documento accesible en tu sitio web que explique qué datos recolectas, para qué los usas, con quién los compartes y cuánto tiempo los conservas. Debe ser específico de tu negocio, no una plantilla copiada de internet.

### 2. Consentimiento informado
Cada formulario que capture datos personales debe incluir un consentimiento explícito (no pre-marcado) que informe a la persona qué hará tu empresa con sus datos.

### 3. Canal para ejercer derechos
Toda persona tiene derecho a Acceder, Rectificar, Cancelar, Oponerse y Portar sus datos (ARCO + portabilidad). Necesitas un canal visible —típicamente un email tipo \`privacidad@tu-empresa.cl\`— donde recibir y responder estas solicitudes en plazos legales.

### 4. Identificación del responsable
Datos de contacto claros del responsable del tratamiento. En PYMEs medianas o que tratan datos sensibles, además, un **Delegado de Protección de Datos (DPO)**.

### 5. Contratos con encargados (DPA)
Si un proveedor —tu agencia de marketing, tu sistema de boletas, tu hosting— trata datos por cuenta tuya, necesitas un contrato firmado que regule ese tratamiento.

## Cuánto pueden cobrar las multas

Las sanciones de la Ley 21.719 son altas:

- **Infracciones leves**: hasta 5.000 UTM (~$350 millones CLP)
- **Infracciones graves**: hasta 10.000 UTM (~$700 millones CLP)
- **Infracciones gravísimas**: hasta **20.000 UTM** (~$1.400 millones CLP)
- **Reincidencia**: hasta el **4% de los ingresos anuales** de la empresa

## El plazo de gracia para PYMEs

Buena noticia: las **PYMEs tienen un año de gracia**. Durante los primeros 12 meses desde la entrada en vigencia (diciembre 2026 – diciembre 2027), las micro, pequeñas y medianas empresas solo recibirán **amonestaciones**, no multas.

Esto te da margen, pero no es excusa para postergar. Si llega una fiscalización o un reclamo y no tienes tus documentos en regla, igual te llega la amonestación. Y al año siguiente, la próxima vez, sí hay multa.

## Cómo prepararte ahora

1. **Escanea tu sitio web** con [el escáner gratuito de Ley de Datos Chile](/) para detectar qué te falta.
2. **Genera tu Política de Privacidad** adecuada a la Ley 21.719 con nuestro generador guiado.
3. **Configura un canal de derechos** (un email visible para que las personas ejerzan sus derechos).
4. **Revisa tus formularios** y agrega cláusulas de consentimiento explícitas.
5. **Documenta** todo. La APDP fiscaliza evidencia operativa, no solo papeles.

La ley no es opcional y no se va a postergar. Empezar ahora es barato; empezar en noviembre de 2026 es una carrera contra el reloj.
`,
  },
  {
    slug: "politica-privacidad-ley-21719-que-debe-incluir",
    cover: "/blog/politica-privacidad-ley-21719-que-debe-incluir.png",
    title: "Política de Privacidad para empresas chilenas: qué debe incluir según la Ley 21.719",
    description: "Estructura obligatoria de la Política de Privacidad bajo la Ley 21.719: secciones, datos del responsable, finalidades, derechos ARCO y más.",
    keywords: "política de privacidad, Ley 21.719, política de privacidad Chile, PYME, plantilla política privacidad",
    date: "2026-02-12",
    readingMinutes: 6,
    author: "Equipo Ley de Datos Chile",
    excerpt: "Tu Política de Privacidad no es un trámite: es el documento que define cómo tu empresa cumple con la Ley 21.719. Aquí están las secciones obligatorias que debe incluir.",
    content: `## Por qué la Política de Privacidad es obligatoria

La Ley N° 21.719 exige que toda empresa que trate datos personales publique una **Política de Privacidad** accesible, clara y específica de su operación. No basta con copiar una plantilla de internet: el documento debe **reflejar realmente** cómo tu negocio trata datos.

Si llega una fiscalización de la **Agencia de Protección de Datos Personales (APDP)** y tu política dice cosas que no haces —o no dice cosas que sí haces— quedas expuesto.

## Secciones obligatorias

### 1. Identificación del Responsable del Tratamiento

Debe incluir:
- Razón social y RUT de la empresa.
- Domicilio legal.
- Email de contacto para temas de privacidad.
- Si aplica, nombre y contacto del **Delegado de Protección de Datos (DPO)**.

### 2. Datos personales que se recolectan

Lista clara y específica:
- Datos de identificación (nombre, RUT)
- Datos de contacto (email, teléfono, dirección)
- Datos transaccionales (compras, montos)
- Datos de navegación (cookies, dirección IP)
- Datos sensibles (solo si los tratas y bajo condiciones especiales)

### 3. Finalidades del tratamiento

Para qué usas cada dato. Por ejemplo:
- Procesar pedidos y entregas
- Atender consultas y soporte
- Marketing y comunicaciones (solo con consentimiento expreso)
- Facturación
- Cumplimiento de obligaciones legales

### 4. Base legal del tratamiento

La Ley 21.719 exige una base legal para cada finalidad. Las más comunes son:
- **Consentimiento** del titular
- **Ejecución de un contrato**
- **Cumplimiento de una obligación legal**
- **Interés legítimo** de la empresa

### 5. Plazos de conservación

Cuánto tiempo guardas los datos. Lo más usado:
- Mientras la persona sea cliente
- 5 años por obligación contable (Servicio de Impuestos Internos)
- Período de prescripción de acciones legales

### 6. Terceros con quienes compartes datos

Lista clara de:
- Encargados del tratamiento (hosting, email marketing, CRM, pasarela de pago)
- Cesionarios (si vendes o compartes datos con empresas relacionadas)
- Transferencias internacionales (si tu hosting está fuera de Chile)

### 7. Derechos del titular

Tu Política debe informar sobre los **derechos ARCO + Portabilidad**:
- **Acceso**: saber qué datos tienes sobre la persona
- **Rectificación**: corregir datos incorrectos
- **Cancelación**: pedir eliminación
- **Oposición**: rechazar ciertos tratamientos
- **Portabilidad**: recibir los datos en formato estructurado

Y el canal concreto donde ejercerlos.

### 8. Medidas de seguridad

Sin detalles técnicos sensibles, explica que tomas medidas para proteger los datos (cifrado, control de accesos, copias de seguridad, etc.).

### 9. Uso de cookies

Si tu sitio usa Google Analytics, Meta Pixel u otras herramientas, debes declararlo. Idealmente, con un banner de consentimiento de cookies.

### 10. Cambios a la Política

Cómo y cuándo notificarás los cambios a tus usuarios.

## Errores comunes que la APDP penaliza

- Copiar una política de privacidad española o argentina sin adaptar.
- Decir "no compartimos datos con terceros" cuando usas Mailchimp o Google Analytics.
- Olvidar mencionar el canal de derechos.
- No identificar al Responsable del Tratamiento.
- Política genérica que no menciona tus formularios reales ni tus integraciones reales.

## Cómo generarla rápido y bien

Con Ley de Datos Chile, respondes una **entrevista guiada de 10 preguntas simples** sobre tu negocio y nuestro generador con IA redacta tu Política de Privacidad personalizada, conforme a la Ley 21.719, en menos de 10 minutos. [Genera la tuya aquí](/modulo-pro).
`,
  },
  {
    slug: "multas-ley-datos-chile-cuanto-cuestan",
    cover: "/blog/multas-ley-datos-chile-cuanto-cuestan.png",
    title: "Multas de la Ley de Datos en Chile: cuánto puede costarle a tu PYME no cumplir",
    description: "Detalle de las multas de la Ley 21.719: infracciones leves, graves y gravísimas, y por qué las PYMEs tienen un año de gracia.",
    keywords: "multas Ley 21.719, sanciones protección datos Chile, APDP multas, UTM multas",
    date: "2026-02-14",
    readingMinutes: 5,
    author: "Equipo Ley de Datos Chile",
    excerpt: "Las multas de la Ley 21.719 pueden llegar a $1.400 millones de pesos. Pero hay un período de gracia para PYMEs. Estos son los números reales.",
    content: `## El esquema de multas de la Ley 21.719

La Ley N° 21.719 clasifica las infracciones en tres categorías. Cada una tiene su propio rango de multas, expresadas en **Unidades Tributarias Mensuales (UTM)**.

### Infracciones leves — hasta 5.000 UTM

Aproximadamente **350 millones de pesos chilenos**.

Ejemplos:
- No tener publicada la Política de Privacidad.
- No mantener un canal de derechos del titular operativo.
- No actualizar la información de contacto del Responsable del Tratamiento.

### Infracciones graves — hasta 10.000 UTM

Aproximadamente **700 millones de pesos chilenos**.

Ejemplos:
- Tratar datos personales sin consentimiento cuando se requiere.
- No responder solicitudes ARCO dentro del plazo legal.
- Transferir datos a terceros sin contrato de encargo (DPA).

### Infracciones gravísimas — hasta 20.000 UTM

Aproximadamente **1.400 millones de pesos chilenos**.

Ejemplos:
- Tratamiento masivo de datos sensibles sin protección reforzada.
- Brecha de seguridad no notificada en el plazo de 72 horas.
- Transferencia internacional de datos sin garantías.

### Reincidencia: hasta el 4% de ingresos anuales

Si una empresa reincide en una infracción gravísima, la multa puede escalar al **4% de sus ingresos anuales totales**, similar al modelo GDPR europeo.

## El período de gracia para PYMEs

La ley reconoce que adecuarse cuesta tiempo y dinero. Por eso introdujo un **período de gracia para PYMEs**:

> Durante los primeros 12 meses desde la entrada en vigencia (entre el 1 de diciembre de 2026 y el 1 de diciembre de 2027), las micro, pequeñas y medianas empresas que cometan infracciones leves o graves recibirán **solo amonestaciones**, no multas.

Hay matices importantes:
- Aplica solo a PYMEs según definición de la Ley 20.416.
- **No aplica a infracciones gravísimas**: una brecha no notificada o un tratamiento masivo de datos sensibles sí puede multarte desde el día uno.
- La amonestación es pública y queda en tu registro.

## Qué fiscaliza realmente la APDP

La nueva **Agencia de Protección de Datos Personales (APDP)** no se conforma con que tu Política de Privacidad esté bonita en tu sitio. Fiscaliza:

- **Registros datados**: ¿tienes un inventario de todos los tratamientos que haces?
- **Logs de solicitudes**: ¿registraste cuándo recibiste y respondiste cada solicitud ARCO?
- **Contratos con terceros**: ¿tienes DPA firmados con tus encargados?
- **Notificación de brechas**: si tuviste un incidente, ¿lo notificaste en 72 horas?

La evidencia operativa vale más que el papel.

## Cómo minimizar el riesgo

La mejor forma de evitar una multa es no estar expuesto. Para una PYME típica, el mínimo viable es:

1. **Política de Privacidad real y publicada** que refleje tu negocio.
2. **Consentimiento en formularios** que capturen datos.
3. **Canal de derechos** (un email visible).
4. **Contratos con encargados** (DPA) para tus principales proveedores.
5. **Protocolo simple** de notificación de brechas (qué hacer si tienes un incidente).

Ley de Datos Chile te ayuda con los puntos 1, 2 y 4. El resto es organización interna.

[Empieza con el escáner gratuito](/) para saber exactamente qué te falta.
`,
  },
  {
    slug: "como-cumplir-ley-21719-pymes",
    cover: "/blog/como-cumplir-ley-21719-pymes.png",
    title: "Cómo cumplir la Ley 21.719 en Chile: guía paso a paso para PYMEs (2026)",
    description: "Guía práctica para cumplir la Ley 21.719 de Protección de Datos en Chile antes del 1 de diciembre de 2026. 7 pasos concretos para PYMEs, con plazos, obligaciones y multas explicadas en simple.",
    keywords: "cómo cumplir la Ley 21.719, Ley 21.719 PYMEs, cumplimiento protección de datos Chile, obligaciones Ley 21.719, qué hacer Ley de datos Chile",
    date: "2026-06-20",
    readingMinutes: 9,
    author: "Equipo Ley de Datos Chile",
    excerpt: "La Ley 21.719 entra en vigencia plena el 1 de diciembre de 2026. Cumplir no es tan complejo si lo divides en pasos. Esta guía te muestra exactamente qué hacer, en orden, en lenguaje claro y sin jerga legal.",
    content: `La Ley 21.719 entra en plena vigencia el **1 de diciembre de 2026**, y a diferencia de la antigua ley de 1999, esta sí tiene capacidad real de fiscalizar y multar. Si tu empresa pide datos a sus clientes —un nombre, un correo, un RUT— estás obligado a cumplir, sin importar el tamaño de tu negocio.

La buena noticia: cumplir no es tan complejo como parece si lo divides en pasos. Esta guía te muestra exactamente qué hacer, en orden, en lenguaje que se entiende sin ser abogado.

> **¿No sabes por dónde empezar?** Antes de leer, [escanea tu sitio gratis](/) y descubre en 30 segundos qué te falta para cumplir. Luego vuelve a esta guía con tu informe en mano.

## Antes de empezar: lo que tienes que saber

La Ley 21.719 reemplaza a la antigua Ley 19.628 y crea la **Agencia de Protección de Datos Personales (APDP)**, un organismo con poder para fiscalizar, ordenar correcciones y aplicar multas. Las sanciones llegan hasta 20.000 UTM (más de $1.400 millones) en los casos más graves.

Hay un alivio para las PYMEs: durante el primer año (diciembre 2026 a diciembre 2027), las pequeñas y medianas empresas reciben solo amonestaciones, no multas. Pero eso no es una excusa para dejarlo: la obligación empieza igual, y la ley evalúa **evidencia con fecha** —registros, documentos, procesos— que no se construyen de un día para otro. Lo que hagas hoy es lo que te respalda mañana.

Estos son los 7 pasos para llegar listo.

## Paso 1: Haz un inventario de los datos que manejas

No puedes proteger lo que no sabes que tienes. El primer paso es listar todos los datos personales que tu empresa recolecta y qué hace con ellos.

Pregúntate, proceso por proceso: ¿qué datos pido? (nombre, email, teléfono, RUT, dirección, datos de pago), ¿para qué los uso?, ¿dónde los guardo?, ¿con quién los comparto? Por ejemplo: la base de clientes para facturar, los correos para tu newsletter, los CV de quienes postulan a un trabajo.

Este inventario es la base de todo lo demás. Sin él, los pasos siguientes no tienen sobre qué construirse.

## Paso 2: Define la base legal de cada tratamiento

Aquí hay un error común: pensar que todo necesita consentimiento. No es así. La ley reconoce varias **bases de licitud**, y elegir la correcta para cada caso es clave.

Las principales son el **consentimiento** (la persona acepta libremente), la **ejecución de un contrato** (necesitas el dato para cumplir un servicio, como la dirección para despachar un pedido), la **obligación legal** (una ley te exige tratar el dato, como registros tributarios o laborales) y el **interés legítimo** (un fin legítimo del negocio que no pasa por encima de los derechos de la persona).

Elegir mal la base legal invalida todo el tratamiento, así que este paso conviene hacerlo con cuidado y, en casos complejos, con apoyo legal. Un dato importante: el **consentimiento tácito ya no vale**. Tiene que ser libre, informado, específico e inequívoco; no sirve la casilla pre-marcada ni el "si sigues navegando, aceptas".

## Paso 3: Crea o actualiza tu política de privacidad

Tu política de privacidad debe informar con claridad quién es el responsable de los datos, qué datos recolectas, para qué, con qué base legal, con quién los compartes, por cuánto tiempo los guardas y cuáles son los derechos de las personas.

Una política copiada de internet casi nunca cumple, porque no refleja tu negocio ni está alineada con la ley chilena. Tiene que ser específica de lo que tu empresa realmente hace.

> Puedes [generar tu política de privacidad personalizada](/modulo-pro) respondiendo unas preguntas simples sobre tu negocio, conforme a la Ley 21.719, sin esperar semanas ni pagar miles por un abogado.

## Paso 4: Arregla el consentimiento en tu sitio y formularios

Revisa cada punto donde recoges datos: formularios de contacto, suscripción al newsletter, registro de clientes, checkout. En cada uno necesitas un mecanismo de consentimiento que sea claro, específico y fácil de revocar.

Esto incluye el **banner de cookies** de tu sitio. Si usas Google Analytics, Meta Pixel u otras herramientas de seguimiento, esas cargan cookies que requieren consentimiento antes de activarse. El banner no es decorativo: debe efectivamente impedir que esos scripts corran hasta que la persona acepte.

La forma de implementarlo depende de tu plataforma (WordPress, Shopify y otras tienen métodos distintos), pero el principio es el mismo: informar, pedir permiso, y respetar la respuesta.

## Paso 5: Arma tu Catálogo de Datos Personales (RAT)

El Catálogo de Datos Personales (también conocido como RAT, por su sigla en otros marcos legales) es un documento interno obligatorio que la Agencia puede pedirte en cualquier momento. La Guía Práctica de la Secretaría de Gobierno Digital (Min. Segpres, dic 2025) lo denomina formalmente "Catálogo". Es un registro de cada actividad de tratamiento que incluye la finalidad, la base legal, las categorías de datos, los destinatarios, los plazos de conservación y las medidas de seguridad aplicadas.

Piénsalo como la versión formal y ordenada del inventario del Paso 1. No es algo que escribes una vez y olvidas: hay que mantenerlo actualizado a medida que tu negocio cambia. Es una de las piezas de "evidencia datada" que más mira la fiscalización.

## Paso 6: Prepara los procedimientos de derechos y de brechas

Hay dos procesos que tienes que tener listos **antes** de necesitarlos, porque ambos corren contra el reloj.

El primero es responder a los **derechos de los titulares** (acceso, rectificación, cancelación, oposición y portabilidad). Cualquier persona puede pedirte ver, corregir o borrar sus datos, y debes responder en plazos acotados. Necesitas un canal claro para recibir estas solicitudes y un procedimiento para responderlas a tiempo.

El segundo es el **protocolo de brechas**. Si sufres una vulneración de seguridad que afecte datos personales, la ley exige notificar a la Agencia dentro de las **72 horas** desde que tienes conocimiento del incidente. Si la brecha implica un riesgo alto, también debes avisar a las personas afectadas. Setenta y dos horas es muy poco tiempo para improvisar: el procedimiento tiene que estar escrito y probado de antemano.

## Paso 7: Revisa tus contratos con proveedores (DPA)

Casi toda PYME comparte datos con terceros sin pensarlo: el CRM, la plataforma de email marketing, el servicio de nómina, el hosting. Cada proveedor que procesa datos en tu nombre necesita un **contrato de tratamiento de datos (DPA)** que establezca sus obligaciones, las medidas de seguridad y qué pasa si hay una brecha.

Si tus contratos actuales no tienen estas cláusulas, están fuera de cumplimiento. Y ojo con un detalle frecuente: guardar datos de tus clientes en herramientas como un CRM o un servicio de correo cuyos servidores están en el extranjero cuenta como **transferencia internacional de datos**, que tiene sus propias reglas.

## ¿Necesito un DPO (Delegado de Protección de Datos)?

El DPO es la persona responsable de supervisar el cumplimiento dentro de la organización. No todas las PYMEs están obligadas a tenerlo, pero si tratas datos sensibles, operas en sectores como salud, finanzas o educación, o manejas grandes volúmenes de datos, probablemente lo necesites.

Para una PYME, la opción de un **DPO externo** suele ser más viable económicamente que contratar a alguien de planta. Incluso cuando no es obligatorio, tener un responsable claro de protección de datos puede ser la diferencia entre el cumplimiento ordenado y una multa.

## Resumen: tu checklist para cumplir la Ley 21.719

Para llegar listo al 1 de diciembre de 2026, esto es lo que tu PYME necesita tener: un inventario de los datos que maneja; la base legal definida para cada tratamiento; una política de privacidad actualizada y específica; consentimiento correcto en formularios y un banner de cookies que funcione; el Catálogo de Datos Personales (también conocido como RAT); procedimientos listos para responder derechos y notificar brechas en 72 horas; y contratos (DPA) con todos los proveedores que tocan tus datos.

Parece mucho, pero se hace por partes. Lo importante es empezar ahora, porque la evidencia de cumplimiento se construye con tiempo, no de un día para otro.

## Empieza hoy, en 30 segundos

La forma más rápida de saber en qué estás parado es revisar tu propio sitio. [Escanea tu sitio web gratis con Ley de Datos Chile](/) y obtén un informe inmediato de qué cumples y qué te falta, con recomendaciones concretas para cada punto. Es gratis y no necesitas instalar nada.

---

*Este artículo es de carácter informativo y orientativo, y no constituye asesoría legal. Para el cumplimiento formal de la Ley 21.719 en tu caso particular, consulta con un abogado especialista en protección de datos.*
`,
  },
  {
    slug: "marketing-digital-ley-21719",
    cover: "/blog/marketing-digital-ley-21719.png",
    title: "Marketing Digital y Ley 21.719: Guía por Canal 2026",
    description: "Email, WhatsApp, redes sociales, formularios: qué cambia en cada canal de marketing digital bajo la Ley 21.719 y qué puedes seguir haciendo sin riesgo.",
    keywords: "marketing digital ley 21719 chile, ley 21719 publicidad, ley 21719 campañas, consentimiento marketing chile",
    date: "2026-02-20",
    readingMinutes: 7,
    author: "Equipo Ley de Datos Chile",
    excerpt: "La Ley 21.719 no prohíbe el marketing digital. Cambia cómo obtienes el consentimiento, qué documentas y qué pasa con los datos después. Esta guía resume las reglas comunes a todos los canales y enlaza al detalle de cada uno.",
    content: `**Resumen rápido**: la Ley 21.719 no prohíbe el marketing digital — email, WhatsApp, redes sociales y formularios siguen siendo canales válidos. Lo que cambia es cómo obtienes el consentimiento, qué documentas y qué pasa con los datos después de capturarlos. Esta guía resume las reglas comunes a todos los canales y enlaza al detalle de cada uno.

## Por qué el marketing es el área de mayor riesgo práctico

De todas las áreas que toca la Ley 21.719 — RRHH, sistemas internos, proveedores — el marketing digital es probablemente donde una PYME genera más tratamiento de datos personales sin darse cuenta. Cada formulario de contacto, cada pixel de seguimiento, cada lista de WhatsApp y cada campaña de email es, técnicamente, un tratamiento de datos que necesita una base legal.

Esto no es exclusivo de empresas grandes con equipos de marketing dedicados. Un emprendimiento que vende por Instagram y coordina pedidos por WhatsApp ya está tratando datos personales todos los días.

## Las tres reglas que aplican a todos los canales

Antes de entrar al detalle por canal, hay tres principios que se repiten en email, WhatsApp, redes sociales y formularios por igual:

1. **Necesitas una base legal, no solo "tener el dato".** La base más común en marketing es el consentimiento, pero no es la única — la ejecución de un contrato (por ejemplo, confirmar un pedido) puede bastar sin pedir un consentimiento adicional.
2. **El consentimiento, cuando lo usas, debe ser específico.** No sirve un consentimiento genérico de "acepto el tratamiento de mis datos" que cubra todo. Si vas a usar el email para newsletter Y para retargeting, son dos finalidades distintas.
3. **Debes poder probarlo.** Ante una fiscalización, la carga de la prueba es tuya: tienes que poder mostrar cuándo, cómo y para qué se obtuvo cada consentimiento.

## Resumen por canal

**Email marketing** — la base legal depende de si es un cliente existente (interés legítimo suele bastar) o un lead frío (necesitas consentimiento explícito). El error más común es comprar bases externas sin verificar su origen. [Lee la guía completa →](/blog/email-marketing-interes-legitimo-consentimiento-ley-21719)

**WhatsApp Business** — responder a quien te escribe primero no requiere el mismo tratamiento que enviar campañas de difusión masiva. Agregar clientes a un grupo sin preguntarles es de las infracciones más claras y fáciles de fiscalizar. [Lee la guía completa →](/blog/whatsapp-business-ley-21719)

**Redes sociales y retargeting (Meta Pixel, Google Ads)** — el problema técnico más común es que estos scripts se activan automáticamente al cargar la página, antes de que el usuario decida algo. Eso es tratamiento sin consentimiento previo. [Lee la guía completa →](/blog/meta-pixel-retargeting-ley-21719)

**Formularios, sorteos y promociones** — el riesgo principal es pedir más datos de los necesarios y no tener un checkbox de consentimiento separado y no premarcado. [Lee la guía completa →](/blog/formularios-sorteos-datos-personales-ley-21719)

**Bases de datos de clientes y fidelización** — guardar datos "para siempre, por si acaso" ya no es una opción. Cada categoría de dato necesita un plazo de conservación razonable. [Lee la guía completa →](/blog/bases-clientes-fidelizacion-que-datos-guardar-ley-21719)

Y si tu empresa califica como PYME, hay un matiz importante sobre cómo se sanciona tu primera infracción que vale la pena conocer antes de seguir. [PYME: amonestación o multa en tu primera infracción →](/blog/pyme-amonestacion-vs-multa-ley-21719)

## Lo que NO cambia

Vale la pena ser claros en lo que no es: la ley no obliga a dejar de hacer marketing digital, no prohíbe el retargeting, no impide enviar newsletters ni hacer campañas en WhatsApp. Lo que exige es **documentar y poder demostrar** cómo obtienes y usas los datos — la diferencia entre cumplir y no cumplir rara vez está en la estrategia de marketing en sí, sino en el flujo de consentimiento detrás de ella.

> **Escanea tu sitio gratis** y revisa qué señales de marketing digital podrían ser un problema. [Probar el escáner →](/)

## Preguntas frecuentes

**¿Necesito un DPO solo para hacer marketing digital?**
No necesariamente. La designación de un Delegado de Protección de Datos depende de si tu empresa trata datos sensibles o a gran escala, no del solo hecho de hacer marketing. Para la mayoría de las PYMEs, no es obligatorio, aunque tener un responsable interno informal ayuda a ordenar el cumplimiento.

**¿Qué pasa con los datos que ya tenía antes de que la ley entrara en vigencia?**
Esos datos siguen siendo válidos si fueron obtenidos de forma lícita en su momento, pero a partir de la entrada en vigencia debes poder demostrar la base legal bajo la cual los sigues usando, y dar la posibilidad de oposición o revocación a quienes corresponda.

**¿Todas las reglas son iguales para email, WhatsApp y redes sociales?**
Los principios generales (base legal, finalidad específica, proporcionalidad) son los mismos, pero cada canal tiene matices propios — por ejemplo, WhatsApp distingue entre comunicación reactiva y campañas de difusión, algo que no aplica igual a un formulario web.

---

*Este artículo es de carácter informativo y orientativo, y no constituye asesoría legal. Para el cumplimiento formal de la Ley 21.719 en tu caso particular, consulta con un abogado especialista en protección de datos.*
`,
  },
  {
    slug: "meta-pixel-retargeting-ley-21719",
    cover: "/blog/meta-pixel-retargeting-ley-21719.png",
    title: "Meta Pixel y Retargeting bajo la Ley 21.719 en Chile",
    description: "¿Tu Meta Pixel o Google Ads se activan antes de pedir consentimiento? Te explicamos qué exige la Ley 21.719 para retargeting y cómo corregirlo.",
    keywords: "meta pixel ley 21719 chile, retargeting consentimiento chile, google ads ley 21719, cookies de seguimiento chile",
    date: "2026-02-19",
    readingMinutes: 6,
    author: "Equipo Ley de Datos Chile",
    excerpt: "Meta Pixel, Google Ads y herramientas similares recolectan datos personales y, bajo la Ley 21.719, necesitan consentimiento previo y específico antes de activarse — no después. La mayoría de los sitios chilenos hoy los activan al cargar la página.",
    content: `**Resumen rápido**: Meta Pixel, Google Ads y herramientas similares de retargeting recolectan datos personales (identificadores, comportamiento de navegación) y, bajo la Ley 21.719, necesitan consentimiento previo y específico antes de activarse — no después. La mayoría de los sitios chilenos hoy los activan automáticamente al cargar la página, lo que ya es un problema bajo la nueva ley.

## El problema técnico que casi nadie revisa

Cuando una agencia o un desarrollador instala Meta Pixel o una etiqueta de Google Ads, lo normal es ponerla en el \`<head>\` del sitio para que se dispare apenas alguien entra. Funciona perfecto para medir conversiones — y es exactamente lo que la Ley 21.719 ya no permite hacer sin consentimiento previo.

El pixel no es un detalle técnico neutro: recolecta identificadores del navegador, comportamiento de navegación y, combinado con tu base de clientes, puede asociar esa actividad a una persona identificable. Eso es tratamiento de datos personales desde la primera carga de página, no desde que alguien hace clic en "comprar".

## Qué exige la ley exactamente

El consentimiento bajo la Ley 21.719 tiene que ser libre, específico, informado e inequívoco — y obtenido **antes** de cualquier tratamiento, no después. Aplicado a retargeting, esto significa:

- El pixel no puede dispararse al cargar la página por defecto.
- El usuario debe tener una opción real de rechazar (no un banner que solo informa, sino uno que efectivamente bloquea el script hasta que se acepte).
- Si usas el mismo pixel para varias finalidades (medir conversión, crear audiencias, remarketing), lo ideal es que el usuario pueda elegir cuáles acepta.

## La transferencia internacional que nadie menciona

Hay una segunda capa del problema que suele pasar desapercibida: Meta y Google procesan estos datos fuera de Chile. Eso convierte el uso del pixel también en una transferencia internacional de datos, que requiere su propia base legal — normalmente, garantías contractuales con el proveedor (los términos de tratamiento de datos que Meta y Google ya ofrecen, pero que hay que revisar y aceptar activamente, no asumir que ya están cubiertos).

## Qué sí puedes seguir haciendo

La ley no prohíbe el retargeting. Lo que cambia es el orden de las cosas: primero consentimiento, después tratamiento. Las soluciones técnicas más comunes para resolver esto:

1. Implementar un banner de consentimiento que efectivamente bloquee la carga de scripts de terceros hasta que el usuario decida.
2. Usar el "modo de consentimiento" que ofrecen Google y Meta, que ajusta el comportamiento del pixel según lo que el usuario haya aceptado.
3. Separar las finalidades: medición básica de tráfico (que puede tener una base legal distinta) versus construcción de audiencias publicitarias (que normalmente sí requiere consentimiento explícito).

## Checklist rápido para revisar tu sitio

1. Abre tu sitio en una ventana de incógnito y revisa, antes de hacer clic en nada, si ya se están cargando scripts de Meta o Google (se puede ver en las herramientas de desarrollador del navegador, pestaña Red).
2. Si tu sitio tiene un banner de cookies, verifica si realmente bloquea los scripts o solo aparece como decoración mientras todo se carga igual.
3. Revisa si tienes un Acuerdo de Tratamiento de Datos vigente con Meta y Google, no solo los términos de uso generales de la cuenta publicitaria.
4. Confirma que tu política de privacidad menciona explícitamente estas herramientas y su finalidad.

> **Escanea tu sitio gratis** y revisa si tus scripts de marketing se activan antes de consentimiento. [Probar el escáner →](/)

## Preguntas frecuentes

**¿Google Analytics tiene el mismo problema que Meta Pixel?**
Sí, en esencia. Cualquier herramienta de analítica o seguimiento que recolecte identificadores y se active automáticamente al cargar la página está sujeta a la misma exigencia de consentimiento previo.

**¿Tengo que desactivar todo el retargeting mientras resuelvo esto?**
No es necesario desactivarlo por completo, pero sí ajustar el orden técnico: que el script espere el consentimiento del usuario antes de dispararse, en vez de cargar automáticamente.

**¿El "modo de consentimiento" de Google es suficiente por sí solo?**
Ayuda bastante porque ajusta automáticamente el comportamiento de las etiquetas según el consentimiento del usuario, pero sigue dependiendo de que tengas un banner real que capture esa decisión — la herramienta por sí sola no reemplaza el consentimiento.

**¿Esto aplica también si solo uso Meta Pixel para medir, no para hacer anuncios?**
Sí. Lo relevante no es si lo usas para publicidad activa, sino si el script recolecta y transmite datos personales — eso ocurre incluso en uso puramente de medición.

[← Volver al hub: Marketing Digital y Ley 21.719](/blog/marketing-digital-ley-21719)

---

*Este artículo es de carácter informativo y orientativo, y no constituye asesoría legal. Para el cumplimiento formal de la Ley 21.719 en tu caso particular, consulta con un abogado especialista en protección de datos.*
`,
  },
  {
    slug: "whatsapp-business-ley-21719",
    cover: "/blog/whatsapp-business-ley-21719.png",
    title: "WhatsApp Business y Ley 21.719: Guía para Empresas Chile",
    description: "¿Puedes seguir enviando mensajes masivos por WhatsApp bajo la Ley 21.719? Te explicamos qué es infracción clara y qué sigue siendo válido.",
    keywords: "whatsapp masivo ley 21719, puedo agregar clientes a grupo whatsapp, whatsapp business datos personales chile",
    date: "2026-02-18",
    readingMinutes: 6,
    author: "Equipo Ley de Datos Chile",
    excerpt: "Un número de teléfono es un dato personal igual que un email. Bajo la Ley 21.719, responder a un cliente que te escribe primero no requiere el mismo tratamiento que enviarle campañas de difusión masiva. Confundirlas es de los errores más comunes.",
    content: `**Resumen rápido**: un número de teléfono es un dato personal igual que un email. Bajo la Ley 21.719, responder a un cliente que te escribe primero no requiere el mismo tratamiento que enviarle campañas de difusión masiva — la diferencia entre ambas situaciones es la base legal que las sostiene, y confundirlas es de los errores más comunes y más fáciles de fiscalizar.

## WhatsApp no es un canal "informal" frente a la ley

Es fácil pensar que WhatsApp, por ser conversacional y personal, queda fuera del radar de una ley de protección de datos pensada para bases de datos y formularios. No es así: el número de teléfono de un cliente, su nombre de perfil y el historial de la conversación son datos personales, exactamente igual que un email en un CRM.

## Lo que es claramente infracción

- **Agregar clientes a un grupo de difusión o de WhatsApp sin preguntarles antes.** Es de las prácticas más comunes en PYMEs chilenas y, bajo la nueva ley, una de las infracciones más fáciles de identificar — el cliente solo tiene que mostrar la captura de pantalla.
- **Comprar o intercambiar bases de números para campañas de difusión.** Si no sabes cómo se obtuvo el consentimiento original de esa lista, no tienes base legal para usarla.
- **Compartir el número de un cliente con un tercero** (otro proveedor, un partner comercial) sin que el cliente lo sepa.

## La distinción que sí importa: reactivo vs. proactivo

No toda comunicación por WhatsApp necesita el mismo nivel de consentimiento. La clave está en distinguir:

**Comunicación reactiva** — el cliente te escribe primero, o te dio su número específicamente para coordinar una compra o un servicio (confirmar un pedido, avisar que el despacho está en camino). Esto generalmente se sostiene en la ejecución de un contrato, sin necesitar un consentimiento adicional separado.

**Comunicación proactiva o de difusión** — tú le escribes primero con fines promocionales, lo agregas a una lista de novedades, o le envías ofertas que no pidió. Esto sí requiere consentimiento explícito y específico, obtenido antes de empezar a enviar ese tipo de mensajes.

## Buenas prácticas concretas

1. **Pide consentimiento explícito antes de agregar a alguien a una lista de difusión** — un simple "¿quieres recibir nuestras novedades por este medio?" con respuesta afirmativa ya es mejor que el silencio.
2. **Da una opción de baja simple** — que decir "no quiero más mensajes" funcione de verdad, no que requiera escribirle a tres personas distintas.
3. **No mezcles WhatsApp personal del dueño con WhatsApp comercial** sin ordenar quién tiene acceso a esas conversaciones y qué pasa con ellas si esa persona deja la empresa.
4. **Si usas herramientas como Wati, Twilio o integraciones de CRM con WhatsApp Business API**, revisa si esos proveedores almacenan los datos fuera de Chile — eso agrega la capa de transferencia internacional a la ecuación.

> **Genera tu Cláusula de Consentimiento** para WhatsApp y otros canales en minutos. [Ver Módulo Pro →](/modulo-pro)

## Preguntas frecuentes

**¿Necesito consentimiento para responder a un cliente que me escribió primero?**
No de la misma forma. Si alguien te escribe para consultar o comprar, esa interacción ya implica una relación donde puedes responder y coordinar la compra sin pedir un consentimiento adicional separado — la base legal ahí es la ejecución de la gestión que el propio cliente inició.

**¿Aplica lo mismo si uso WhatsApp Business API en vez de la app normal?**
El principio legal es el mismo, pero la API suele implicar un proveedor intermediario (como Twilio o Wati) que almacena y procesa los mensajes, lo que agrega una capa adicional: revisar si ese proveedor tiene garantías para transferencia internacional de datos.

**¿Puedo seguir enviando recordatorios de citas o confirmaciones de pedido sin pedir consentimiento extra?**
Generalmente sí, porque ese tipo de mensaje suele sostenerse en la ejecución del contrato o gestión que el cliente ya inició (agendar una hora, comprar un producto) — es distinto a enviar promociones no solicitadas.

**¿Qué pasa si un cliente me pide que deje de escribirle?**
Debes respetarlo y dejar de enviarle comunicaciones de ese tipo. Ignorar una solicitud de baja es, en sí mismo, una infracción al derecho de oposición que la ley reconoce a los titulares de los datos.

[← Volver al hub: Marketing Digital y Ley 21.719](/blog/marketing-digital-ley-21719)

---

*Este artículo es de carácter informativo y orientativo, y no constituye asesoría legal. Para el cumplimiento formal de la Ley 21.719 en tu caso particular, consulta con un abogado especialista en protección de datos.*
`,
  },
  {
    slug: "formularios-sorteos-datos-personales-ley-21719",
    cover: "/blog/formularios-sorteos-datos-personales-ley-21719.png",
    title: "Formularios y Sorteos: Datos Personales bajo Ley 21.719",
    description: "Formularios de cotización, sorteos en Instagram, promociones: cómo capturar datos de clientes sin convertir tu campaña en una infracción.",
    keywords: "formulario captura de datos ley 21719, sorteo instagram datos personales chile, consentimiento formulario web chile",
    date: "2026-02-17",
    readingMinutes: 6,
    author: "Equipo Ley de Datos Chile",
    excerpt: "Cada formulario de contacto, cotización o sorteo es tratamiento de datos personales desde el primer clic. Los dos errores más comunes bajo la Ley 21.719 son pedir más datos de los necesarios y usar un checkbox premarcado.",
    content: `**Resumen rápido**: cada formulario de contacto, cotización o sorteo es tratamiento de datos personales desde el primer clic. Los dos errores más comunes bajo la Ley 21.719 son pedir más datos de los necesarios y usar un checkbox de consentimiento premarcado o ambiguo — ambos son fáciles de corregir y fáciles de fiscalizar si no se corrigen.

## Un formulario no es un trámite neutro

Para una PYME, un formulario de cotización o un sorteo en redes sociales suele verse como un trámite operativo más, no como algo regulado. Pero desde el momento en que alguien escribe su nombre, email o teléfono en ese formulario, ya estás tratando datos personales — y eso activa las mismas obligaciones que cualquier otro canal de marketing.

## El error más común: pedir de más

El principio de proporcionalidad de la Ley 21.719 es simple de enunciar y fácil de violar sin querer: solo puedes pedir los datos estrictamente necesarios para la finalidad declarada. Si tu formulario es para coordinar una cotización, pedir nombre, email y teléfono normalmente basta. Pedir fecha de nacimiento, RUT completo o dirección exacta "por si acaso" es recolectar más de lo necesario, y eso por sí solo puede ser observado en una fiscalización.

## El checkbox de consentimiento: cómo hacerlo bien

Hay tres errores típicos en los checkboxes de consentimiento que conviene revisar hoy mismo:

1. **Casilla premarcada** — el usuario debe marcarla activamente, nunca debe venir aceptada por defecto.
2. **Consentimiento mezclado con los Términos y Condiciones** — aceptar "los términos" en general no equivale a un consentimiento específico para tratamiento de datos con fines de marketing.
3. **Texto vago** — un checkbox que dice solo "acepto" sin explicar para qué se usarán los datos no cumple con el estándar de consentimiento informado.

## Sorteos y concursos: la capa extra de riesgo

Las dinámicas promocionales en redes sociales suelen pedir datos adicionales — RUT para validar identidad del ganador, fecha de nacimiento para verificar edad mínima. Esto puede estar justificado, pero solo si la finalidad es clara y proporcional al sorteo: no es lo mismo pedir el RUT para validar al ganador de un premio que pedirlo a todos los participantes sin necesidad real.

También hay que pensar qué pasa con los datos de quienes **no ganan**: si no tienes una razón para conservarlos después de terminado el sorteo, lo correcto es eliminarlos o anonimizarlos, no dejarlos acumulados indefinidamente en una planilla.

## Checklist antes de lanzar una campaña con formulario

1. ¿Cada dato que pido tiene una razón clara y proporcional a la finalidad del formulario?
2. ¿El checkbox de consentimiento está separado de los Términos y Condiciones, y sin marcar por defecto?
3. ¿El texto explica para qué se usarán los datos, en lenguaje simple?
4. ¿Tengo definido qué pasa con los datos de quienes no avanzan en el proceso (no compran, no ganan el sorteo)?
5. ¿Mi política de privacidad menciona este tipo de campaña específicamente?

> **Genera tu Cláusula de Consentimiento y tu Política de Cookies** para formularios y campañas. [Ver Módulo Pro →](/modulo-pro)

## Preguntas frecuentes

**¿Puedo pedir el RUT en un formulario de cotización simple?**
Solo si tiene una razón funcional clara — por ejemplo, si la cotización requiere facturación inmediata. Si no es necesario para esa etapa, es mejor no pedirlo todavía y solicitarlo más adelante si la venta avanza.

**¿Necesito un checkbox de consentimiento distinto para cada formulario de mi sitio?**
Si cada formulario tiene una finalidad distinta (contacto general versus suscripción a newsletter, por ejemplo), sí conviene tener checkboxes específicos para cada uno, en vez de un consentimiento genérico que cubra todo.

**¿Qué hago con los datos de los participantes de un sorteo que ya terminó?**
Si no tienes una finalidad activa para conservarlos (por ejemplo, que hayan aceptado además recibir novedades), lo recomendable es eliminarlos o anonimizarlos una vez resuelto el sorteo.

**¿Esto aplica también a formularios hechos en Google Forms o Typeform?**
Sí, la herramienta que uses para construir el formulario no cambia la obligación legal. Además, conviene revisar dónde almacena los datos esa herramienta, porque puede implicar una transferencia internacional adicional.

[← Volver al hub: Marketing Digital y Ley 21.719](/blog/marketing-digital-ley-21719)

---

*Este artículo es de carácter informativo y orientativo, y no constituye asesoría legal. Para el cumplimiento formal de la Ley 21.719 en tu caso particular, consulta con un abogado especialista en protección de datos.*
`,
  },
  {
    slug: "email-marketing-interes-legitimo-consentimiento-ley-21719",
    cover: "/blog/email-marketing-interes-legitimo-consentimiento-ley-21719.png",
    title: "Email Marketing bajo Ley 21.719: Consentimiento vs. Interés Legítimo",
    description: "¿Puedes seguir enviando email marketing bajo la Ley 21.719? Te explicamos cuándo basta el interés legítimo y cuándo necesitas consentimiento explícito.",
    keywords: "puedo seguir enviando email marketing ley 21719, base legal email marketing chile, interés legítimo ley 21719",
    date: "2026-02-16",
    readingMinutes: 7,
    author: "Equipo Ley de Datos Chile",
    excerpt: "Sí, puedes seguir haciendo email marketing bajo la Ley 21.719. La pregunta correcta no es '¿necesito consentimiento?' sino '¿cuál es mi base legal para este envío?'. Para clientes existentes, el interés legítimo suele bastar.",
    content: `**Resumen rápido**: sí, puedes seguir haciendo email marketing bajo la Ley 21.719. La pregunta correcta no es "¿necesito consentimiento?" sino "¿cuál es mi base legal para este envío específico?". Para clientes existentes, el interés legítimo suele bastar; para leads fríos o bases compradas, necesitas consentimiento explícito — y confundir ambos casos es el error más caro.

## El mito que conviene desarmar primero

Existe la idea de que, bajo la nueva ley, cada email de marketing requiere un consentimiento explícito y documentado, sin excepción. No es así. La Ley 21.719 reconoce varias bases legales para el tratamiento de datos, y el **interés legítimo** es una de ellas — perfectamente aplicable a buena parte del email marketing que ya hace una PYME con sus propios clientes.

## Las dos bases legales que realmente importan acá

**Interés legítimo** — aplica cuando existe una relación previa razonable con la persona (es cliente tuyo, compró algo, se registró en tu sitio) y el contenido que le envías está relacionado con esa relación: novedades de productos similares, recordatorios, contenido relevante para alguien que ya te conoce. No requiere un consentimiento adicional, pero sí requiere que la persona pueda oponerse fácilmente si no quiere seguir recibiendo esos correos.

**Consentimiento explícito** — es la base que necesitas cuando le escribes a alguien que no tiene relación previa contigo: un lead capturado en una landing page, una base comprada o cedida por un tercero, una lista armada para una campaña fría. Acá no basta con que la persona "no se haya opuesto" — tiene que haber aceptado activamente recibir ese tipo de comunicación.

## Cuándo se cae el interés legítimo como base

El interés legítimo no es un comodín que sirve para cualquier envío. Deja de aplicar cuando:

- Compraste o arrendaste una base de datos externa sin saber cómo se obtuvo el consentimiento original.
- Le envías contenido que no tiene relación con la interacción original que tuvo contigo (por ejemplo, vender algo completamente distinto a quien solo se inscribió a un webinar puntual).
- La persona ya ejerció su derecho de oposición y sigues enviándole correos de todas formas.

## Doble opt-in: la evidencia que te protege

Aunque uses interés legítimo para tu base de clientes existente, para los casos donde sí necesitas consentimiento (formularios de suscripción, leads nuevos) la práctica recomendada es el doble opt-in: la persona se suscribe, y confirma esa suscripción haciendo clic en un email de confirmación. Esto no es solo buena práctica de entregabilidad — es evidencia documentada de que el consentimiento fue real y específico, exactamente lo que tendrías que mostrar ante una fiscalización.

## Qué debes documentar, sea cual sea tu base legal

- Fecha y método de obtención del dato (formulario, compra, registro).
- Versión de la política de privacidad vigente en ese momento.
- Si corresponde, el registro del consentimiento (IP, hora, checkbox marcado).
- Cómo y cuándo alguien ejerció su derecho de oposición, si lo hizo.

## La diferencia con el email transaccional

Vale la pena aclarar algo que genera confusión: los correos transaccionales (confirmación de compra, aviso de despacho, recuperación de contraseña) no se rigen por las mismas reglas que el email marketing, porque no son comunicaciones promocionales — son parte de la ejecución del servicio que la persona ya solicitó.

> **Simula** qué pasaría si tu base legal de email marketing no fuera la correcta. [Probar el simulador →](/simulador-multas)

## Preguntas frecuentes

**¿Puedo seguir enviando newsletter a mis clientes actuales sin pedirles un nuevo consentimiento?**
En general sí, si el contenido está relacionado con la relación comercial que ya tienen contigo y les das una forma fácil de darse de baja — eso corresponde a la base de interés legítimo, no requiere consentimiento adicional.

**¿Qué pasa si compré una base de leads para una campaña?**
Ahí el interés legítimo generalmente no aplica, porque no existe una relación previa con esas personas. Necesitas poder demostrar que esa base fue obtenida con consentimiento válido para ese fin específico — algo que muchas bases compradas no pueden acreditar.

**¿El doble opt-in es obligatorio por ley?**
No está exigido textualmente como único método válido, pero es la forma más sólida de demostrar consentimiento informado y específico si alguna vez tienes que probarlo ante la Agencia.

**¿Cómo sé si mi caso específico debería usar interés legítimo o consentimiento?**
Depende de si existe una relación previa razonable con esa persona y de si el contenido que le envías es coherente con esa relación. En caso de duda, el consentimiento explícito siempre es la opción más segura, aunque implique más fricción inicial.

[← Volver al hub: Marketing Digital y Ley 21.719](/blog/marketing-digital-ley-21719)

---

*Este artículo es de carácter informativo y orientativo, y no constituye asesoría legal. Para el cumplimiento formal de la Ley 21.719 en tu caso particular, consulta con un abogado especialista en protección de datos.*
`,
  },
  {
    slug: "bases-clientes-fidelizacion-que-datos-guardar-ley-21719",
    cover: "/blog/bases-clientes-fidelizacion-que-datos-guardar-ley-21719.png",
    title: "Bases de Clientes y Fidelización: Qué Guardar bajo Ley 21.719",
    description: "¿Qué datos de clientes puedes guardar y por cuánto tiempo bajo la Ley 21.719? Guía práctica para programas de fidelización y bases de datos comerciales.",
    keywords: "qué datos de clientes puedo guardar chile, programa fidelización ley 21719, plazo conservación datos chile",
    date: "2026-02-15",
    readingMinutes: 6,
    author: "Equipo Ley de Datos Chile",
    excerpt: "Tu 'base de clientes' bajo la Ley 21.719 no es solo el CRM — incluye Excel, WhatsApp guardado y libretas físicas. La ley exige guardar solo lo necesario, por el tiempo necesario, y poder explicar para qué sirve cada dato.",
    content: `**Resumen rápido**: tu "base de clientes" bajo la Ley 21.719 no es solo el CRM — incluye planillas Excel, chats de WhatsApp guardados y libretas físicas. La ley no prohíbe tener una base de datos para fidelización, pero exige que solo guardes lo necesario, por el tiempo necesario, y que puedas explicar para qué sirve cada dato que conservas.

## Tu base de clientes es más grande de lo que crees

Cuando se piensa en "la base de datos de la empresa", normalmente se piensa en el CRM o el sistema de ventas. Pero bajo la Ley 21.719, cualquier lugar donde guardes información identificable de una persona cuenta: una planilla Excel con contactos, un chat de WhatsApp guardado con historial de pedidos, incluso una libreta física con nombres y teléfonos de clientes frecuentes.

## El principio de proporcionalidad aplicado a fidelización

Un programa de fidelización típico — puntos, descuentos por frecuencia, cupones de cumpleaños — no tiene ningún problema bajo la nueva ley, siempre que los datos que pidas para inscribir a alguien sean los que realmente necesitas para que el programa funcione. Pedir nombre, email y fecha de cumpleaños para un programa de descuentos por cumpleaños es razonable. Pedir además el RUT completo, dirección exacta y estado civil "para tener el perfil completo" probablemente no lo es, salvo que tengas una razón concreta para necesitarlo.

## Los plazos de conservación: lo que más se ignora en la práctica

Una de las obligaciones menos conocidas de la ley es que los datos no pueden guardarse indefinidamente "por si acaso son útiles después". Cada categoría de dato debe tener un plazo de conservación razonable, relacionado con la finalidad por la que se recolectó:

- Datos de un cliente activo: mientras dure la relación comercial, más el plazo legal de respaldo tributario o contractual que corresponda.
- Datos de un lead que nunca compró: un plazo razonable y acotado, no indefinido.
- Datos de participantes de una promoción puntual: hasta que termine el proceso asociado (validación del ganador, por ejemplo), salvo que hayan dado consentimiento para otra finalidad adicional.

## Qué pasa cuando un cliente deja de comprar

No existe una obligación automática de eliminar inmediatamente los datos de alguien que dejó de comprar, pero sí de tener un criterio claro: si pasado un tiempo razonable de inactividad no tienes una finalidad activa que justifique seguir conservando esos datos (como mantenerlo en una lista de remarketing a la que sí consintió), lo correcto es eliminarlos o anonimizarlos.

## Cómo esto se conecta con el Catálogo de Datos (RAT)

El Catálogo de Datos Personales (conocido como RAT en GDPR y como "Catálogo" en la Guía Práctica oficial Min. Segpres dic 2025) es justamente el documento donde se ordena toda esta información: qué datos guardas, con qué finalidad, por cuánto tiempo, y quién tiene acceso. Para una PYME que nunca ha hecho este ejercicio, suele ser el momento en que se da cuenta de que tiene datos dispersos en más lugares de los que pensaba — y es también la oportunidad de ordenar plazos de conservación que probablemente nunca se habían definido formalmente.

> **Genera tu Catálogo de Datos Personales (RAT)** y ordena tu base de clientes. [Ver Módulo Pro →](/modulo-pro)

## Preguntas frecuentes

**¿Puedo seguir teniendo un Excel con mis clientes en vez de un CRM?**
Sí, la herramienta no es el problema — lo que importa es que ese archivo tenga acceso controlado, un propósito claro y que los datos que contiene estén dentro de los plazos de conservación que definas.

**¿Cuánto tiempo puedo guardar los datos de un lead que nunca compró?**
La ley no fija un número exacto para todos los casos, pero exige que sea un plazo razonable y justificado según la finalidad. Guardarlo indefinidamente sin una razón activa es lo que hay que evitar.

**¿Necesito el consentimiento de mis clientes actuales para tenerlos en un programa de fidelización?**
Si el programa está directamente relacionado con la relación comercial existente, normalmente se sostiene en la ejecución del servicio o el interés legítimo. Si vas a usar esos datos para fines adicionales (por ejemplo, compartirlos con un socio comercial), ahí sí necesitas un consentimiento específico para esa finalidad adicional.

**¿Qué pasa si tengo datos de clientes guardados en WhatsApp Web sin ningún orden?**
Esos chats también son parte de tu tratamiento de datos personales. Vale la pena definir qué información relevante se traspasa a un sistema ordenado (con plazos y acceso definidos) y qué se puede depurar de las conversaciones antiguas que ya no tienen una finalidad activa.

[← Volver al hub: Marketing Digital y Ley 21.719](/blog/marketing-digital-ley-21719)

---

*Este artículo es de carácter informativo y orientativo, y no constituye asesoría legal. Para el cumplimiento formal de la Ley 21.719 en tu caso particular, consulta con un abogado especialista en protección de datos.*
`,
  },
  {
    slug: "gobierno-hoja-de-ruta-ley-datos-pyme",
    cover: "/blog/gobierno-hoja-de-ruta-ley-datos-pyme.png",
    title: "El Gobierno ya tiene su hoja de ruta para la Ley de Datos | ¿Y tu PYME?",
    description: "La Secretaría de Gobierno Digital publicó una guía oficial para implementar la Ley 21.719. Esto es lo que cualquier PYME chilena puede copiar de su plan.",
    keywords: "implementación ley de datos personales chile, Ley 21.719, hoja de ruta cumplimiento, PYME chile, SGD guía",
    date: "2026-02-23",
    readingMinutes: 6,
    author: "Equipo Ley de Datos Chile",
    excerpt: "Mientras muchas empresas privadas todavía no han oído hablar de la Ley 21.719, el propio Estado ya publicó su plan de implementación paso a paso. Te mostramos qué puede copiar tu PYME.",
    content: `## El Gobierno ya tiene su hoja de ruta para la Ley de Datos: esto es lo que tu PYME puede copiar

Si todavía no has hecho nada para preparar a tu empresa frente a la Ley N° 21.719, no estás solo. Pero hay un dato que debería cambiar tu sensación de urgencia: **el propio Estado chileno ya se está preparando, con un plan formal, plazos definidos y un comité ejecutivo.**

La Secretaría de Gobierno Digital (SGD) publicó una guía práctica dirigida a los órganos públicos para implementar la nueva ley antes de su entrada en vigencia plena, el **1 de diciembre de 2026**. El documento es interesante no solo por lo que dice, sino por lo que revela: si las instituciones del Estado —con equipos legales, presupuesto y años de anticipación— necesitan una hoja de ruta estructurada, una PYME sin departamento legal probablemente la necesita todavía más.

## Qué propone la guía oficial

El plan del Estado se organiza en etapas progresivas, partiendo por lo organizativo y avanzando hacia lo técnico:

1. **Designar a una persona responsable** de coordinar la implementación dentro de la organización.
2. **Levantar información**: mapear qué datos personales se tratan, con qué fines, dónde se almacenan y con quién se comparten.
3. **Generar un informe de hallazgos**: identificar brechas respecto a lo que la ley exige publicar y declarar.
4. **Formar un comité** que priorice acciones y revise los documentos antes de aprobarlos.
5. **Elaborar los instrumentos formales**: un catálogo de datos tratados, una política de tratamiento de datos, y protocolos específicos para los riesgos más relevantes.

Todo esto, con un cronograma sugerido que va de enero a noviembre de 2026 — es decir, terminando justo antes de que la ley entre en vigencia plena.

## Por qué esto le importa a tu empresa

El plazo final es el mismo para todos: **1 de diciembre de 2026**. Da igual si eres un ministerio o una tienda online con cinco empleados — ese día, los estándares de la ley aplican.

La buena noticia es que una PYME no necesita un comité de diez personas ni meses de reuniones para llegar preparada. Puede comprimir la misma lógica en cuatro pasos:

**1. Nombra a alguien responsable (aunque seas tú mismo).**
No se necesita un cargo nuevo ni un delegado de protección de datos para la mayoría de las PYMEs. Basta con que una persona —típicamente el dueño, gerente general o responsable de TI/marketing— sepa que el tema existe y lo siga.

**2. Mapea qué datos manejas, en una versión simple.**
No necesitas una matriz de 16 columnas como la del Estado. Basta con responder: ¿qué datos recolecto (nombre, email, RUT, tarjetas)?, ¿para qué los uso?, ¿con qué herramientas externas los comparto (Mailchimp, Stripe, Google Analytics)?, ¿los transfiero fuera de Chile?

**3. Genera los documentos clave.**
La política de tratamiento de datos personales que el Estado redacta a mano, en tu caso se puede generar en minutos con el contexto de tu negocio ya incorporado.

**4. Mantén el cumplimiento visible y actualizado.**
La ley no es un trámite de una sola vez. Igual que el Estado revisa su comité cada cierto tiempo, tu empresa debería poder demostrar — a clientes, proveedores o fiscalizadores — que su cumplimiento sigue vigente.

> **¿Quieres saber dónde está parada tu empresa hoy?** [Escanea tu sitio web gratis en 30 segundos](/) y obtén un score de cumplimiento con los hallazgos priorizados.

## El atajo que no tiene el Estado

Una institución pública necesita meses para redactar y aprobar cada documento, porque pasa por comités, revisiones legales y jefaturas. Una PYME no tiene esa estructura — pero tampoco la necesita. Lo que sí necesita es generar los mismos instrumentos (política de privacidad, cláusulas de consentimiento, contrato con encargados de datos, registro de actividades, política de cookies, protocolo de brechas) de forma rápida y con el respaldo de la Ley 21.719 citada en cada documento.

> **Genera tus documentos legales en minutos**, personalizados con los datos de tu empresa y tu rubro. Política de privacidad, DPA, RAT, cláusulas de cookies y más, listos para publicar. [Conocer el Módulo Pro →](/modulo-pro)

## Preguntas frecuentes

**¿Mi empresa privada tiene que seguir la misma guía que usa el Estado?**
No de forma literal — esa guía fue escrita específicamente para órganos de la Administración del Estado, con estructuras de comité y gobernanza que no aplican a una PYME. Pero la lógica de fondo (responsable designado, mapeo de datos, documentos formales, revisión periódica) sí es trasladable y útil para cualquier empresa.

**¿Qué pasa si mi empresa no hace nada antes del 1 de diciembre de 2026?**
A partir de esa fecha, la Agencia de Protección de Datos Personales podrá fiscalizar y aplicar sanciones que, en infracciones graves, pueden llegar hasta las 20.000 UTM. Empezar antes reduce tanto el riesgo de sanción como el costo de implementación apurada.

## Lectura relacionada

- [Cronograma Ley 21.719: la versión simplificada para PYMEs chilenas](/blog/cronograma-ley-21719-pyme)
- [Los 12 puntos del Art. 14 ter: checklist para tu sitio web](/blog/checklist-articulo-14-ter-ley-datos)

---

*Este artículo es informativo y no constituye asesoría legal. Para casos específicos, consulta con un abogado especializado en protección de datos.*

**Fuente oficial:** [Guía Práctica para la implementación de la Ley de Protección de Datos Personales — Secretaría de Gobierno Digital](https://wikiguias.digital.gob.cl/datos-personales/guia-practica-implementacion-nueva-ley-datos-personales)
`,
  },
  {
    slug: "cronograma-ley-21719-pyme",
    cover: "/blog/cronograma-ley-21719-pyme.png",
    title: "Cronograma Ley 21.719: la versión simplificada para PYMEs chilenas",
    description: "El Gobierno publicó un cronograma de implementación de 7 fases para el sector público. Te mostramos la versión adaptada en 4 fases para una empresa privada.",
    keywords: "plazo ley de datos personales chile 2026, cronograma ley 21719, fases implementación PYME",
    date: "2026-02-24",
    readingMinutes: 5,
    author: "Equipo Ley de Datos Chile",
    excerpt: "Del cronograma oficial de 7 fases que sigue el Estado, sacamos una versión de 4 fases pensada para que una PYME llegue preparada al 1 de diciembre de 2026 sin perder meses en burocracia.",
    content: `## Cronograma Ley 21.719: la versión simplificada para PYMEs chilenas

Quedan menos meses de los que parece. La Ley N° 21.719 entra en vigencia plena el **1 de diciembre de 2026**, y el cronograma oficial que publicó la Secretaría de Gobierno Digital para los organismos públicos — pensado para repartir el trabajo entre enero y noviembre de ese año — es una buena referencia de cuánto tiempo realmente toma hacer las cosas bien.

Aquí adaptamos ese cronograma a una versión realista para una PYME, sin comités ni meses de levantamiento de información.

## El cronograma oficial (resumido)

La guía del Gobierno organiza el trabajo en 7 etapas:

| Etapa | Contenido | Plazo sugerido (sector público) |
|---|---|---|
| 1 | Primeros pasos (designar responsable, formar proyecto) | Dic. 2025 – Ene. 2026 |
| 2 | Levantamiento de información sobre datos tratados | Ene. – Abr. 2026 |
| 3 | Informe de hallazgos y brechas | Abr. 2026 |
| 4 | Constitución de un comité ejecutivo | Abr. 2026 |
| 5 | Catálogo de datos personales tratados | May. – Jun. 2026 |
| 6 | Política de tratamiento de datos | Jul. 2026 |
| 7 | Protocolos y procedimientos específicos | Ago. – Nov. 2026 |

Es un plan ordenado, pero pensado para una estructura con divisiones, jefaturas y procesos de aprobación. Una empresa de 5 a 50 personas puede comprimir esto drásticamente.

## La versión PYME: 4 fases, no 7

**Fase 1 — Diagnóstico (1 semana)**
En vez de un levantamiento de información con matrices de 16 columnas, una PYME puede partir con un diagnóstico automatizado de su sitio web: qué tan expuesta está hoy, qué señales técnicas faltan (política de privacidad, banner de cookies, transferencias internacionales sin declarar, formularios sin consentimiento).

> **Diagnóstico gratis en 30 segundos.** Pega la URL de tu sitio y obtén un score 0–100 con los hallazgos priorizados. [Escanear mi sitio →](/)

**Fase 2 — Documentos esenciales (1–2 semanas)**
En vez de redactar desde cero una política de tratamiento, una política de cookies, un DPA y un protocolo de brechas — un proceso que para el Estado toma meses de revisión — una PYME puede generarlos directamente con el contexto de su negocio, en formato descargable y listo para publicar.

**Fase 3 — Publicación y ajustes (1 semana)**
Subir los documentos al sitio, ajustar el banner de cookies, revisar los formularios de captura de datos y asegurarse de que las herramientas de terceros (Mailchimp, Google Analytics, Stripe) estén correctamente declaradas.

**Fase 4 — Mantenimiento (continuo)**
La ley no es un evento único. Las novedades regulatorias, los plazos de renovación y la necesidad de mantener los documentos actualizados son responsabilidad permanente — no algo que se resuelve una vez y se olvida.

## El factor tiempo real

El cronograma oficial dedica **casi un año completo** solo a las etapas organizativas y documentales (sin contar los protocolos técnicos, que se extienden hasta noviembre). Una PYME que use herramientas pensadas para acelerar este proceso puede recorrer un camino equivalente en semanas, no meses — lo que importa especialmente si recién estás por empezar y el plazo de diciembre de 2026 ya se siente cerca.

> **No tienes que redactar nada desde cero.** Con el Módulo Pro generas tus documentos legales (política de privacidad, DPA, RAT, cláusulas de cookies, protocolo de brechas y más) en una sola sesión, con descarga en PDF, Word o Markdown. [Conocer el Módulo Pro →](/modulo-pro)

## Preguntas frecuentes

**¿Si mi empresa empieza recién en 2026, todavía alcanzo?**
Sí, especialmente si usas herramientas que aceleran el diagnóstico y la generación de documentos. El cronograma largo del Estado responde a su complejidad organizacional, no a que el proceso en sí sea lento.

**¿Qué pasa si publico los documentos antes de tener todo perfecto?**
Es preferible tener una política de privacidad y un protocolo básico publicados pronto, e irlos mejorando, que esperar a tener todo "perfecto" y llegar después del 1 de diciembre de 2026 sin nada.

## Lectura relacionada

- [El Gobierno ya tiene su hoja de ruta para la Ley de Datos](/blog/gobierno-hoja-de-ruta-ley-datos-pyme)
- [Los 12 puntos del Art. 14 ter: checklist para tu sitio web](/blog/checklist-articulo-14-ter-ley-datos)

---

*Este artículo es informativo y no constituye asesoría legal. Para casos específicos, consulta con un abogado especializado en protección de datos.*

**Fuente oficial:** [Guía Práctica para la implementación de la Ley de Protección de Datos Personales — Secretaría de Gobierno Digital](https://wikiguias.digital.gob.cl/datos-personales/guia-practica-implementacion-nueva-ley-datos-personales)
`,
  },
  {
    slug: "checklist-articulo-14-ter-ley-datos",
    cover: "/blog/checklist-articulo-14-ter-ley-datos.png",
    title: "Los 12 puntos del Art. 14 ter: checklist para tu sitio web (Ley 21.719)",
    description: "El artículo 14 ter de la Ley 21.719 exige publicar 12 tipos de información en tu sitio web. Revisa el checklist completo y descubre si tu empresa ya cumple.",
    keywords: "qué debe tener mi política de privacidad chile, art 14 ter ley 21719, checklist política privacidad",
    date: "2026-02-25",
    readingMinutes: 7,
    author: "Equipo Ley de Datos Chile",
    excerpt: "La guía oficial del Gobierno detalla los 12 puntos exactos que cualquier responsable de datos debe tener publicados y disponibles. Te los explicamos uno por uno, en lenguaje simple.",
    content: `## Los 12 puntos del artículo 14 ter: el checklist que tu sitio web debe cumplir antes de diciembre 2026

Si tu empresa tiene un sitio web que recolecta datos de clientes —un formulario de contacto, un checkout, un newsletter—, hay un artículo específico de la Ley N° 21.719 que probablemente no estás cumpliendo todavía: el **artículo 14 ter**, sobre el deber de información y transparencia.

Este artículo no es una declaración genérica de buenas intenciones. Es un listado concreto de **12 tipos de información** que toda empresa que trata datos personales (el "responsable de datos") debe mantener permanentemente disponible en su sitio web o medio equivalente. La guía oficial de la Secretaría de Gobierno Digital lo usa, de hecho, como el checklist base para evaluar el nivel de cumplimiento de cualquier organización — y es exactamente el mismo estándar que aplica al sector privado.

## El checklist, explicado en lenguaje simple

**1. Tu política de tratamiento de datos, con fecha y versión.**
No basta con tenerla — debe estar identificada con cuándo se publicó y qué versión es.

**2. Quién es el responsable y, si existe, quién es el encargado de prevención.**
Nombre de la empresa, representante legal, y si tienes una persona a cargo de la prevención de riesgos en esta materia.

**3. Un canal de contacto claro.**
Dirección, correo electrónico o formulario donde un titular de datos pueda ejercer sus derechos o hacer consultas.

**4. Qué datos tratas, para qué, de quién, y bajo qué base legal.**
Esto incluye: tipos de datos, descripción de a quiénes pertenecen, a quién se los podrías comunicar, para qué los usas, y la justificación legal de ese uso (consentimiento, ejecución de un contrato, interés legítimo, etc.).

**5. Tus medidas de seguridad.**
Una descripción —aunque sea general— de cómo proteges los datos que almacenas.

**6. Los derechos del titular: acceso, rectificación, supresión, oposición y portabilidad.**
Explicar que cualquier persona puede pedir ver, corregir, eliminar u oponerse al uso de sus datos.

**7. El derecho a reclamar ante la Agencia de Protección de Datos Personales.**
Si tu empresa no responde o rechaza una solicitud, el titular puede acudir a la futura Agencia.

**8. Si transfieres datos a otro país, decirlo.**
Y si ese país no tiene un nivel de protección equivalente al chileno, qué garantías existen para justificar esa transferencia (por ejemplo, cláusulas contractuales).

**9. Cuánto tiempo conservas los datos.**
No es necesario un plazo exacto al día, pero sí un criterio claro de retención.

**10. De dónde vienen los datos.**
Si los recolectaste directamente del titular o de una fuente de acceso público.

**11. Si el tratamiento se basa en consentimiento, decir que se puede retirar en cualquier momento.**
Y que retirarlo no afecta lo que ya se hizo antes de esa fecha.

**12. Si usas decisiones automatizadas o perfilamiento, explicarlo.**
Por ejemplo, si tu sistema de scoring de clientes, recomendaciones o segmentación toma decisiones sin intervención humana.

> **¿Quieres saber cuántos de estos 12 puntos ya cumples?** Nuestro escáner gratuito revisa tu sitio en 30 segundos y te muestra exactamente qué falta. [Escanear mi sitio gratis →](/)

## Por qué este checklist importa más que otros

A diferencia de otras obligaciones de la ley que son más generales, el artículo 14 ter es **específico y verificable**: o tu sitio tiene esa información publicada, o no la tiene. Es, literalmente, lo primero que revisaría un fiscalizador — y lo primero que nota un cliente o socio comercial que evalúa si trabajar contigo.

La mayoría de las políticas de privacidad genéricas que circulan en internet (plantillas descargadas, textos copiados de otra empresa) no cubren los 12 puntos. Suelen quedarse en lo básico —qué datos se recolectan— y omiten partes como las transferencias internacionales, el derecho a reclamar ante la Agencia, o el tratamiento automatizado.

> **Genera una política de privacidad que cubre los 12 puntos del artículo 14 ter**, personalizada con los datos de tu empresa, tu rubro y las herramientas que usas. Incluye auditoría automática que te indica si quedó algo pendiente. [Generar mi política de privacidad →](/modulo-pro)

## Preguntas frecuentes

**¿Esto aplica solo a empresas grandes?**
No. La ley no distingue por tamaño de empresa. Si tratas datos personales de clientes, empleados o proveedores, el deber de información aplica — sea que tengas 3 empleados o 300.

**¿Dónde debo publicar esta información?**
La forma más común es una página de "Política de Privacidad" enlazada desde el footer del sitio, aunque la ley permite cualquier medio de información equivalente, mientras esté permanentemente disponible al público.

**¿Qué pasa si solo cumplo algunos de los 12 puntos?**
Es mejor que no tener nada, pero no es suficiente. El artículo exige que la información esté disponible "a lo menos" con ese contenido — es decir, los 12 puntos son el mínimo, no una lista opcional.

## Lectura relacionada

- [¿Necesitas un RAT en Chile? Lo que aclara la guía oficial](/blog/rat-registro-actividades-tratamiento-chile)
- [Diccionario de la Ley de Protección de Datos en Chile](/blog/diccionario-ley-proteccion-datos-chile)

---

*Este artículo es informativo y no constituye asesoría legal. Para casos específicos, consulta con un abogado especializado en protección de datos.*

**Fuente oficial:** [Guía Práctica para la implementación de la Ley de Protección de Datos Personales — Secretaría de Gobierno Digital](https://wikiguias.digital.gob.cl/datos-personales/guia-practica-implementacion-nueva-ley-datos-personales)
`,
  },
  {
    slug: "rat-registro-actividades-tratamiento-chile",
    cover: "/blog/rat-registro-actividades-tratamiento-chile.png",
    title: "¿Necesitas un RAT en Chile? Lo que aclara la guía oficial del Gobierno",
    description: "La Ley 21.719 no exige explícitamente un Registro de Actividades de Tratamiento (RAT), pero la guía del Gobierno explica por qué igual conviene tenerlo. Te lo explicamos.",
    keywords: "RAT ley de datos chile, registro actividades tratamiento, catálogo datos personales chile",
    date: "2026-02-26",
    readingMinutes: 5,
    author: "Equipo Ley de Datos Chile",
    excerpt: "Existe confusión sobre si el RAT es obligatorio en Chile. La guía oficial de la Secretaría de Gobierno Digital aclara el punto exacto — y por qué tenerlo igual te conviene.",
    content: `## ¿Necesitas un Registro de Actividades de Tratamiento (RAT) en Chile? Lo que aclara la guía oficial

Si has investigado sobre la Ley N° 21.719, probablemente te topaste con el término "RAT" — Registro de Actividades de Tratamiento. Es un concepto tomado del RGPD europeo, y genera una confusión razonable: **¿es obligatorio en Chile o no?**

La guía oficial de la Secretaría de Gobierno Digital responde esta pregunta de forma directa, y vale la pena entenderla bien antes de decidir si tu empresa necesita uno.

## Lo que dice la ley, exactamente

A diferencia de otras normativas internacionales, la **Ley 21.719 no establece expresamente la obligación de mantener un Registro de Actividades de Tratamiento** como instrumento formal con ese nombre.

Sin embargo —y este es el matiz importante— el artículo 14 ter sí exige que los responsables de datos publiquen información muy específica sobre los tratamientos que realizan: qué datos tratan, a quién pertenecen, a quién se los comunican, para qué los usan, bajo qué base legal, y de dónde provienen.

En otras palabras: **el nombre "RAT" no aparece en la ley, pero el contenido que normalmente tiene un RAT sí es exigido**, solo que repartido dentro del deber de información y transparencia.

## Por qué la guía recomienda tenerlo igual

La propia guía del Gobierno reconoce este punto y recomienda a los organismos públicos construir un "catálogo" de datos tratados — que en la práctica cumple la misma función que un RAT — precisamente porque ordena toda la información que la ley exige publicar, evitando tener que reconstruirla cada vez que cambia algo en la empresa.

Para una PYME, la lógica es la misma. Sin un documento centralizado que liste qué datos tratas, con qué fines y bajo qué base legal, es muy difícil mantener actualizada tu política de privacidad cada vez que agregas una nueva herramienta, cambias de proveedor de hosting o lanzas un nuevo formulario de captura de leads.

## Qué debería contener un RAT, en la práctica

Tomando como referencia el mismo contenido que exige la ley a través del artículo 14 ter, un RAT bien construido para una PYME debería registrar:

- Las categorías de datos que tratas (nombre, RUT, email, datos de pago, etc.)
- A qué tipo de personas pertenecen esos datos (clientes, empleados, postulantes, suscriptores)
- A quién se los comunicas o cedes (proveedores, plataformas de pago, herramientas de marketing)
- Para qué los usas, de forma específica
- La base legal de cada tratamiento (consentimiento, ejecución de contrato, interés legítimo)
- Cuánto tiempo los conservas
- De dónde provienen (formulario directo, compra de base de datos, fuente pública)

> **¿Tu sitio ya muestra señales de que faltan estos elementos?** Escanéalo gratis y descubre qué transferencias o tratamientos no están declarados. [Escanear mi sitio →](/)

## El riesgo de no tenerlo

Aunque el RAT como documento formal no sea obligatorio por nombre, **no tener claridad interna sobre qué datos tratas es, en la práctica, el primer punto de falla** frente a cualquier fiscalización o solicitud de un cliente. Si la Agencia de Protección de Datos te pide acreditar cómo tratas los datos de tus usuarios, no tener un registro ordenado significa partir de cero, bajo presión y con plazos cortos.

> **Genera tu Registro de Actividades de Tratamiento en minutos**, con la información de tu empresa ya incorporada desde tu perfil. Incluye auditoría automática que valida si cubriste todos los puntos exigidos por el artículo 14 ter. [Generar mi RAT →](/modulo-pro)

## Preguntas frecuentes

**¿Entonces el RAT es obligatorio o no?**
No existe como instrumento obligatorio con ese nombre específico en la Ley 21.719. Pero el contenido que normalmente incluye sí es exigido a través del deber de información del artículo 14 ter, por lo que en la práctica equivale a tener que mantener uno.

**¿Cada cuánto debo actualizar mi RAT?**
Cada vez que cambias un proveedor, agregas una nueva herramienta que trata datos, o modificas la finalidad de un tratamiento existente. No es un documento estático.

## Lectura relacionada

- [Los 12 puntos del Art. 14 ter: checklist para tu sitio web](/blog/checklist-articulo-14-ter-ley-datos)
- [Transferencias internacionales de datos: lo que exige la Ley 21.719](/blog/transferencias-internacionales-datos-clausulas-modelo)

---

*Este artículo es informativo y no constituye asesoría legal. Para casos específicos, consulta con un abogado especializado en protección de datos.*

**Fuente oficial:** [Guía Práctica para la implementación de la Ley de Protección de Datos Personales — Secretaría de Gobierno Digital](https://wikiguias.digital.gob.cl/datos-personales/guia-practica-implementacion-nueva-ley-datos-personales)
`,
  },
  {
    slug: "transferencias-internacionales-datos-clausulas-modelo",
    cover: "/blog/transferencias-internacionales-datos-clausulas-modelo.png",
    title: "Transferencias internacionales de datos en Chile: qué exige la Ley 21.719",
    description: "Si usas Mailchimp, AWS, Stripe o Google, probablemente transfieres datos fuera de Chile sin saberlo. Te explicamos qué exige la ley y qué son las cláusulas contractuales modelo.",
    keywords: "transferencia internacional de datos chile ley, cláusulas contractuales modelo, mailchimp aws ley 21719",
    date: "2026-02-27",
    readingMinutes: 6,
    author: "Equipo Ley de Datos Chile",
    excerpt: "Si tu empresa usa herramientas extranjeras para guardar datos de clientes, probablemente ya estás haciendo una transferencia internacional de datos sin saberlo. Esto exige la ley al respecto.",
    content: `## Transferencias internacionales de datos: lo que exige la Ley 21.719 (y probablemente no sabías que aplicaba a ti)

Si tu empresa usa Mailchimp para enviar newsletters, AWS o Google Cloud para alojar tu sitio, o Stripe para procesar pagos, hay una alta probabilidad de que estés haciendo algo que la ley regula directamente: **una transferencia internacional de datos personales**, sin haberlo declarado en ningún lado.

No se trata de un escenario exótico reservado a multinacionales. Es, de hecho, la situación de la mayoría de las PYMEs chilenas con presencia digital.

## Qué exige la ley exactamente

El artículo 14 ter de la Ley 21.719 obliga a informar, cuando corresponda, **si se transfieren datos personales a un tercer país u organización internacional**, y si ese destino ofrece o no un nivel adecuado de protección equivalente al chileno. Si no lo ofrece, la empresa debe poder explicar qué garantías existen que igual justifiquen esa transferencia.

En términos simples: no está prohibido enviar datos al extranjero, pero sí está prohibido hacerlo en silencio, sin informarlo y sin un respaldo contractual adecuado.

## Las cláusulas contractuales modelo

Para resolver el problema de "qué garantías son válidas", el Ministerio de Economía, Fomento y Turismo aprobó recientemente un set de **Cláusulas Contractuales Modelo para Transferencias Internacionales**, pensadas específicamente para este escenario: cuando una empresa chilena envía datos a un proveedor en un país sin nivel de protección adecuado, estas cláusulas ofrecen un marco contractual estandarizado para justificar esa transferencia.

Esto es exactamente lo mismo que resolvieron las cláusulas contractuales tipo de la Unión Europea para el RGPD — Chile ahora cuenta con su propia versión, adaptada a la Ley 21.719.

## Cómo saber si tu empresa hace transferencias internacionales

La mayoría de las PYMEs lo hacen sin notarlo, a través de herramientas cotidianas:

- **Email marketing**: Mailchimp, Brevo, ActiveCampaign (servidores en EE.UU. o Europa)
- **Hosting y almacenamiento**: AWS, Google Cloud, Azure
- **Pagos**: Stripe, PayPal
- **CRM y soporte**: HubSpot, Zendesk, Intercom
- **Analítica**: Google Analytics
- **Formularios y encuestas**: Typeform, Google Forms

Si tu empresa usa cualquiera de estas herramientas para procesar datos de clientes chilenos, técnicamente estás transfiriendo esos datos fuera de Chile cada vez que se almacenan en sus servidores.

> **Nuestro escáner detecta automáticamente** si tu sitio usa herramientas con transferencias internacionales y si las tienes correctamente declaradas en tu política de privacidad. [Escanear mi sitio gratis →](/)

## Qué debe hacer tu empresa al respecto

1. **Identificar qué herramientas con servidores en el extranjero usas** para procesar datos personales.
2. **Declararlo explícitamente** en tu política de privacidad, indicando el país de destino.
3. **Verificar o incorporar garantías contractuales** — como las cláusulas contractuales modelo — especialmente con proveedores que no ofrecen un nivel de protección equivalente.
4. **Reflejarlo en tu contrato con encargados de datos (DPA)**, si el proveedor trata los datos en tu nombre.

> **Genera tu Contrato con Encargado de Datos (DPA)** personalizado, incorporando automáticamente las herramientas y transferencias detectadas en tu rubro. [Generar mi DPA →](/modulo-pro)

## Preguntas frecuentes

**¿Tengo que dejar de usar herramientas extranjeras?**
No. La ley no prohíbe usar proveedores fuera de Chile, exige transparencia y garantías adecuadas sobre esas transferencias, no su eliminación.

**¿Qué pasa si mi proveedor está en Estados Unidos o Europa?**
Depende del nivel de protección que ese país ofrezca según los criterios de la futura Agencia de Protección de Datos. Mientras eso se define con detalle, la recomendación práctica es declarar la transferencia e incorporar cláusulas contractuales como respaldo.

**¿Esto aplica si solo uso Google Analytics?**
Sí. Cualquier herramienta que procese datos personales de tus visitantes y los almacene fuera de Chile cuenta como transferencia internacional, sin importar si es gratuita o de pago.

## Lectura relacionada

- [¿Necesitas un RAT en Chile? Lo que aclara la guía oficial](/blog/rat-registro-actividades-tratamiento-chile)
- [Diccionario de la Ley de Protección de Datos en Chile](/blog/diccionario-ley-proteccion-datos-chile)

---

*Este artículo es informativo y no constituye asesoría legal. Para casos específicos, consulta con un abogado especializado en protección de datos.*

**Fuente oficial:** [Guía Práctica para la implementación de la Ley de Protección de Datos Personales — Secretaría de Gobierno Digital](https://wikiguias.digital.gob.cl/datos-personales/guia-practica-implementacion-nueva-ley-datos-personales)
`,
  },
  {
    slug: "necesito-delegado-proteccion-datos-dpo-chile",
    cover: "/blog/necesito-delegado-proteccion-datos-dpo-chile.png",
    title: "¿Tu PYME necesita un Delegado de Protección de Datos (DPO)? | Ley 21.719",
    description: "Muchas PYMEs temen que la Ley 21.719 las obligue a contratar un Delegado de Protección de Datos. Te explicamos cuándo es obligatorio y cuándo basta con designar a un responsable.",
    keywords: "delegado de protección de datos chile obligatorio, DPO PYME chile, ley 21719 DPO",
    date: "2026-02-28",
    readingMinutes: 5,
    author: "Equipo Ley de Datos Chile",
    excerpt: "¿Necesitas contratar un cargo especializado para cumplir con la Ley 21.719? La respuesta, según la propia guía del Gobierno, depende de un matiz importante que casi nadie explica bien.",
    content: `## ¿Tu PYME necesita un Delegado de Protección de Datos? Lo que dice la ley (y lo que aplica en la práctica)

Uno de los miedos más comunes entre dueños de PYME al enterarse de la Ley 21.719 es pensar que tendrán que contratar a un especialista a tiempo completo solo para cumplir con la normativa. La buena noticia: **en la gran mayoría de los casos, no es así.**

La guía oficial de la Secretaría de Gobierno Digital, aunque está escrita para el sector público, aclara con bastante precisión la diferencia entre dos figuras que suelen confundirse: el **encargado o responsable interno** y el **Delegado de Protección de Datos (DPO)**.

## La diferencia clave

**El encargado o responsable interno** es simplemente la persona dentro de la organización que coordina la implementación de la ley: levanta información, organiza los documentos, hace seguimiento. No requiere formación especializada obligatoria ni un cargo de exclusiva dedicación — puede ser, perfectamente, el mismo gerente general o quien hoy lleva el área de TI o administración en una PYME.

**El Delegado de Protección de Datos**, en cambio, es una figura más formal y exigente: la ley le pide cumplir con requisitos técnicos específicos, además de autonomía e independencia dentro de la organización para ejercer su rol sin presiones internas.

## ¿Cuándo es obligatorio el DPO?

La designación de un Delegado de Protección de Datos es, en general, **voluntaria** según la propia normativa — las organizaciones pueden optar por designar uno de forma proactiva, pero no están obligadas a hacerlo salvo en los casos específicos que la futura Agencia de Protección de Datos determine (típicamente asociados a tratamientos masivos, datos sensibles a gran escala, o ciertos organismos públicos).

Para la mayoría de las PYMEs chilenas —que no tratan datos sensibles a gran escala ni hacen perfilamiento masivo— **no designar un DPO formal no es un incumplimiento**, siempre que exista una persona responsable que asuma el seguimiento del cumplimiento.

## Qué necesita realmente tu empresa

En lugar de preguntarte "¿necesito contratar un DPO?", la pregunta más útil para una PYME es: **¿alguien en mi empresa sabe que esto existe, y le toca a alguien hacer seguimiento?**

Eso se resuelve con tres cosas simples:

1. Designar internamente —aunque sea informalmente— a una persona responsable (dueño, gerente, encargado de marketing o TI).
2. Que esa persona tenga acceso a los documentos legales actualizados de la empresa (política de privacidad, RAT, DPA).
3. Que exista un canal claro para que un cliente ejerza sus derechos (acceso, rectificación, eliminación) y alguien sepa qué hacer cuando llegue esa solicitud.

> **Antes de pensar en cargos o contrataciones, revisa qué tan lejos estás del cumplimiento básico.** [Escanea tu sitio gratis en 30 segundos →](/)

## Lo que sí deberías tener, aunque no contrates a nadie

Aunque no necesites un DPO formal, sí necesitas los documentos que la ley exige tener publicados y disponibles — eso no depende de si tienes o no un cargo especializado, sino de si tu empresa trata datos personales (que casi cualquier empresa con clientes hace).

> **Genera tu política de privacidad, RAT y demás documentos legales** sin necesidad de contratar a un abogado o especialista dedicado. El Módulo Pro incorpora automáticamente el contexto de tu rubro y tamaño de empresa. [Conocer el Módulo Pro →](/modulo-pro)

## Preguntas frecuentes

**¿Qué pasa si igual quiero designar un DPO de forma voluntaria?**
Es una opción válida, especialmente si tu empresa trata grandes volúmenes de datos sensibles. En ese caso, esa persona debe cumplir con los requisitos de autonomía e independencia que exige la ley, y normalmente asume las funciones del responsable interno, evitando duplicar roles.

**¿El responsable interno tiene responsabilidad legal personal?**
La responsabilidad legal del cumplimiento recae sobre la empresa (el "responsable de datos"), no se traslada automáticamente a la persona designada internamente para coordinar el tema.

## Lectura relacionada

- [El Gobierno ya tiene su hoja de ruta para la Ley de Datos](/blog/gobierno-hoja-de-ruta-ley-datos-pyme)
- [¿Necesitas un RAT en Chile? Lo que aclara la guía oficial](/blog/rat-registro-actividades-tratamiento-chile)

---

*Este artículo es informativo y no constituye asesoría legal. Para casos específicos, consulta con un abogado especializado en protección de datos.*

**Fuente oficial:** [Guía Práctica para la implementación de la Ley de Protección de Datos Personales — Secretaría de Gobierno Digital](https://wikiguias.digital.gob.cl/datos-personales/guia-practica-implementacion-nueva-ley-datos-personales)
`,
  },
  {
    slug: "protocolo-respuesta-brechas-seguridad-72-horas",
    cover: "/blog/protocolo-respuesta-brechas-seguridad-72-horas.png",
    title: "Brechas de seguridad: el protocolo de 72 horas que exige la Ley 21.719",
    description: "Si tu empresa sufre una filtración de datos, la Ley 21.719 establece plazos estrictos para responder. Te explicamos qué debe tener tu protocolo de respuesta a brechas.",
    keywords: "protocolo brecha de seguridad ley datos chile, notificación brecha 72 horas, art 33 ley 21719",
    date: "2026-03-01",
    readingMinutes: 5,
    author: "Equipo Ley de Datos Chile",
    excerpt: "Una filtración de datos no es solo un problema técnico — es un problema legal con plazos estrictos. Esto exige la Ley 21.719 cuando ocurre una brecha de seguridad.",
    content: `## Brechas de seguridad: el protocolo de respuesta en 72 horas que exige la Ley 21.719

Una filtración de datos —una base de clientes expuesta, un acceso no autorizado a tu sistema, un proveedor que pierde información que le confiaste— no es solo un problema técnico. Bajo la Ley N° 21.719, es también un problema legal con **plazos estrictos de respuesta**, y no tener un protocolo definido antes de que ocurra es, en sí mismo, una falla de cumplimiento.

## Qué exige la ley

El artículo 33 de la ley establece la obligación de notificar las brechas de seguridad que afecten datos personales dentro de un plazo acotado —72 horas— a la autoridad correspondiente, además de informar a los titulares afectados cuando exista un riesgo relevante para sus derechos.

La lógica detrás de este plazo es simple: cuanto antes se detecta y comunica una brecha, antes se puede contener el daño, tanto para los titulares de los datos como para la reputación de la empresa.

## El problema de improvisar en el momento

El error más común de las PYMEs no es no tener un protocolo perfecto, sino **no tener ninguno**. Cuando ocurre una brecha real, no hay tiempo para definir desde cero quién debe enterarse primero, cómo se documenta el incidente, ni qué se le dice a los clientes — y las 72 horas corren igual.

## Qué debe contener un protocolo de respuesta a brechas

Un protocolo bien armado, alineado con los estándares que exige la ley, debería cubrir:

1. **Cómo se detecta y reporta internamente un incidente** (quién debe enterarse primero, por qué canal).
2. **Cómo se evalúa la gravedad** — si hay riesgo real para los derechos de los titulares.
3. **El proceso de notificación a la autoridad** dentro del plazo de 72 horas.
4. **Cómo y cuándo se comunica a los titulares afectados**, si corresponde.
5. **Medidas de contención inmediata** (bloqueo de accesos, cambio de credenciales, aislamiento de sistemas).
6. **Registro del incidente**, incluyendo qué pasó, qué se hizo y qué medidas se tomaron para evitar que se repita.

La guía oficial del Gobierno también hace referencia a la Norma Técnica de Seguridad de la Información y Ciberseguridad (Decreto N° 7 de 2023), que sirve como marco de referencia razonable para definir qué medidas de seguridad adoptar antes de que ocurra cualquier incidente — no solo para responder, sino para reducir la probabilidad de que pase.

> **¿Tu sitio tiene las protecciones básicas de seguridad en orden?** Nuestro escáner revisa señales clave (HTTPS, headers de seguridad, formularios sin protección) en 30 segundos. [Escanear mi sitio →](/)

## No es solo un documento — es preparación real

Tener un protocolo de respuesta a brechas escrito no evita que ocurra un incidente, pero sí determina qué tan caótica o controlada es la respuesta cuando sucede. Las empresas que tienen este protocolo antes de necesitarlo responden más rápido, documentan mejor, y reducen tanto el riesgo legal como el daño reputacional.

> **Genera tu Protocolo de Respuesta a Brechas** personalizado con los datos y herramientas de tu empresa, listo para activar el día que lo necesites. [Generar mi protocolo de brechas →](/modulo-pro)

## Preguntas frecuentes

**¿El plazo de 72 horas corre desde que ocurre la brecha o desde que la descubro?**
La lógica del estándar (consistente con marcos internacionales similares) es que el plazo corre desde que la empresa toma conocimiento del incidente, no desde el momento exacto en que ocurrió — lo que hace aún más importante tener mecanismos de detección temprana.

**¿Toda brecha de seguridad debe notificarse?**
La obligación de notificar aplica cuando existe un riesgo relevante para los derechos de los titulares afectados. Tener un protocolo claro ayuda precisamente a evaluar rápido ese nivel de riesgo, sin tener que improvisarlo en medio de la crisis.

## Lectura relacionada

- [Los 12 puntos del Art. 14 ter: checklist para tu sitio web](/blog/checklist-articulo-14-ter-ley-datos)
- [¿Tu PYME necesita un Delegado de Protección de Datos?](/blog/necesito-delegado-proteccion-datos-dpo-chile)

---

*Este artículo es informativo y no constituye asesoría legal. Para casos específicos, consulta con un abogado especializado en protección de datos.*

**Fuente oficial:** [Guía Práctica para la implementación de la Ley de Protección de Datos Personales — Secretaría de Gobierno Digital](https://wikiguias.digital.gob.cl/datos-personales/guia-practica-implementacion-nueva-ley-datos-personales)
`,
  },
  {
    slug: "diccionario-ley-proteccion-datos-chile",
    cover: "/blog/diccionario-ley-proteccion-datos-chile.png",
    title: "Diccionario de la Ley de Datos en Chile: 9 términos clave explicados simple",
    description: "Dato personal, dato sensible, titular, responsable, tratamiento... Te explicamos en lenguaje simple los 9 términos que más se repiten en la Ley 21.719.",
    keywords: "qué es un dato personal chile, glosario ley 21719, términos protección datos chile",
    date: "2026-03-02",
    readingMinutes: 6,
    author: "Equipo Ley de Datos Chile",
    excerpt: "Antes de generar tus documentos legales, conviene entender el vocabulario básico de la Ley 21.719. Aquí lo explicamos en lenguaje simple, con ejemplos de negocio real.",
    content: `## Diccionario de la Ley de Protección de Datos en Chile: 9 términos que debes conocer

Si vas a generar los documentos legales de tu empresa o simplemente quieres entender qué exige la Ley N° 21.719, hay un puñado de términos que se repiten constantemente — y que conviene tener claros antes de avanzar. Aquí te los explicamos en lenguaje simple, con ejemplos aplicados a un negocio real.

## 1. Dato personal

Cualquier información que permita identificar, directa o indirectamente, a una persona. No es solo el nombre o el RUT — también cuenta una dirección de email, un número de teléfono, una IP, o incluso una combinación de datos que en conjunto permitan reconocer a alguien.

*Ejemplo: el email que un cliente deja en tu formulario de contacto es un dato personal, aunque no incluya su nombre.*

## 2. Dato personal sensible

Una categoría especial de datos personales que la ley protege con mayor exigencia: origen étnico o racial, afiliación política o sindical, situación socioeconómica, convicciones religiosas o filosóficas, datos de salud, datos biométricos, orientación sexual e identidad de género.

*Ejemplo: si tu empresa pide información de salud para una evaluación de seguro, eso es un dato sensible y requiere un tratamiento más cuidadoso.*

## 3. Titular de datos

La persona a quien pertenecen los datos personales. Es quien tiene derecho a saber qué se hace con su información y a ejercer derechos sobre ella.

*Ejemplo: cada cliente que se registra en tu tienda online es un titular de datos.*

## 4. Responsable de datos

La empresa o entidad que decide para qué se usan los datos personales y cómo se tratan, sin importar si los procesa directamente o a través de un proveedor externo.

*Ejemplo: tu empresa es el responsable de los datos de tus clientes, aunque uses Mailchimp para enviar los correos.*

## 5. Tratamiento de datos

Cualquier operación que se haga con datos personales: recolectarlos, almacenarlos, comunicarlos, analizarlos o eliminarlos. Es un concepto amplio que cubre prácticamente todo lo que una empresa hace con la información de sus clientes.

*Ejemplo: guardar el email de un cliente en tu CRM ya es, técnicamente, un tratamiento de datos.*

## 6. Encargado de tratamiento

Un tercero que trata datos personales por cuenta del responsable, siguiendo sus instrucciones — no decide para qué se usan, solo los procesa.

*Ejemplo: tu proveedor de hosting o tu plataforma de email marketing actúan como encargados de tratamiento de los datos que tú les entregas.*

## 7. Elaboración de perfiles

El uso automatizado de datos personales para evaluar, predecir o analizar aspectos como el comportamiento, las preferencias, la situación económica o la ubicación de una persona.

*Ejemplo: un sistema que clasifica automáticamente a tus clientes según su probabilidad de compra está haciendo elaboración de perfiles.*

## 8. Base de legitimidad (o habilitante legal)

La justificación legal que permite tratar un dato personal: puede ser el consentimiento del titular, la ejecución de un contrato, una obligación legal, o un interés legítimo de la empresa.

*Ejemplo: cuando un cliente acepta tus términos para comprar en tu tienda, esa aceptación es la base de legitimidad para tratar sus datos de envío.*

## 9. Transferencia internacional de datos

El envío de datos personales a un tercer país o a una organización internacional, sea porque se almacenan en servidores fuera de Chile o porque un proveedor extranjero los procesa.

*Ejemplo: si usas Google Analytics o AWS, probablemente ya haces transferencias internacionales de datos sin haberlo declarado.*

> **¿Quieres ver estos conceptos aplicados a tu propio sitio?** Escanéalo gratis y descubre qué tratamientos, transferencias y datos sensibles detecta nuestro análisis. [Escanear mi sitio gratis →](/)

## De la teoría a la práctica

Entender estos términos es el primer paso, pero la ley exige plasmarlos en documentos concretos: una política de privacidad que declare tus bases de legitimidad, un DPA que regule la relación con tus encargados de tratamiento, un RAT que liste tus tratamientos de datos.

> **Genera los documentos legales que exige la Ley 21.719**, con estos conceptos ya aplicados al contexto específico de tu empresa y rubro. [Conocer el Módulo Pro →](/modulo-pro)

## Preguntas frecuentes

**¿Necesito memorizar estos términos para cumplir con la ley?**
No, pero entenderlos ayuda a leer tus propios documentos legales con criterio, y a detectar si una política de privacidad genérica realmente aplica a tu negocio o es solo una plantilla copiada de internet.

**¿Estos términos son iguales a los del RGPD europeo?**
Son muy similares, ya que la Ley 21.719 se inspiró en buena medida en estándares internacionales como el RGPD. Pero las definiciones exactas y las obligaciones asociadas son propias de la legislación chilena.

## Lectura relacionada

- [Los 12 puntos del Art. 14 ter: checklist para tu sitio web](/blog/checklist-articulo-14-ter-ley-datos)
- [Transferencias internacionales de datos: lo que exige la Ley 21.719](/blog/transferencias-internacionales-datos-clausulas-modelo)

---

*Este artículo es informativo y no constituye asesoría legal. Para casos específicos, consulta con un abogado especializado en protección de datos.*

**Fuente oficial:** [Guía Práctica para la implementación de la Ley de Protección de Datos Personales — Secretaría de Gobierno Digital](https://wikiguias.digital.gob.cl/datos-personales/guia-practica-implementacion-nueva-ley-datos-personales)
`,
  },
];

export const getPostBySlug = (slug) => BLOG_POSTS.find((p) => p.slug === slug);
